import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { Card } from '../components/ui/card';
import { eventsService } from '../services/eventsService';
import { Event } from '../components/events/EventTableRow';
import { LoadingSpinner } from '../components/events';

const EventDetail: React.FC = () => {
    const { txHash } = useParams<{ txHash: string }>();
    const navigate = useNavigate();
    const [event, setEvent] = useState<Event | null>(null);
    const [loading, setLoading] = useState(true);
    const [copiedField, setCopiedField] = useState<string>('');

    useEffect(() => {
        const fetchEventDetail = async () => {
            if (!txHash) return;

            try {
                setLoading(true);
                const eventData = await eventsService.getEventByTxHash(txHash);
                setEvent(eventData);
            } catch (error) {
                console.error('Error fetching event detail:', error);
                setEvent(null);
            } finally {
                setLoading(false);
            }
        };

        fetchEventDetail();
    }, [txHash]);

    const handleCopy = (text: string, field: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(''), 2000);
    };

    const formatTimestamp = (unixTimestamp: number | undefined) => {
        if (!unixTimestamp) return 'N/A';
        const date = new Date(unixTimestamp * 1000);
        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata',
        });
    };

    const formatTimestampForEventData = (timestamp: number): string => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp * 1000);
        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata',
        });
    };

    const capitalizeKey = (key: string): string => {
        let result = key;
        result = result.replace(/Id$/g, 'ID');
        result = result.replace(/Id([A-Z])/g, 'ID$1');
        result = result.replace(/^ekyc/i, 'EKYC');
        result = result.replace(/([a-z])Ekyc/g, '$1EKYC');
        const words = result.replace(/([A-Z])/g, ' $1').trim();
        let capitalized = words.charAt(0).toUpperCase() + words.slice(1);
        while (capitalized.match(/([A-Z])\s+([A-Z])/)) {
            capitalized = capitalized.replace(/([A-Z])\s+([A-Z])/g, '$1$2');
        }
        return capitalized;
    };

    const renderValue = (value: any, depth: number = 0): React.ReactNode => {
        if (value === null || value === undefined) {
            return <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-400 rounded text-xs">null</span>;
        }

        if (typeof value === 'boolean') {
            return <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${value ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>{String(value)}</span>;
        }

        if (typeof value === 'number') {
            if (value > 1000000000 && value < 3000000000) {
                return <span className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 rounded text-xs border border-green-200 font-medium">{formatTimestampForEventData(value)}</span>;
            }
            return <span className="inline-flex items-center px-2 py-1 bg-brand-light text-brand rounded text-xs border border-blue-200 font-medium">{value}</span>;
        }

        if (typeof value === 'string') {
            if ((value.startsWith('[') && value.endsWith(']')) || (value.startsWith('{') && value.endsWith('}'))) {
                try {
                    const parsed = JSON.parse(value);
                    return renderValue(parsed, depth);
                } catch {
                    // Not valid JSON
                }
            }

            if (/^0x[a-fA-F0-9]+$/.test(value)) {
                return <span className="inline-flex items-center px-2 py-1 bg-purple-50 text-purple-700 font-mono text-xs rounded border border-purple-200">{value}</span>;
            }
            return <span className="inline-flex items-center px-2 py-1 bg-gray-50 text-gray-800 rounded text-xs border border-gray-200">{value}</span>;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                return <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-400 rounded text-xs">empty array</span>;
            }
            return (
                <div className="inline-block ml-4 space-y-2">
                    {value.map((item, index) => (
                        <div key={index} className="flex items-center gap-2 bg-slate-50 rounded p-2 border border-slate-200 w-fit">
                            <span className="inline-flex items-center px-1.5 py-1 bg-slate-200 text-slate-700 rounded text-xs font-medium min-w-[24px] text-center">{index}</span>
                            {renderValue(item, depth + 1)}
                        </div>
                    ))}
                </div>
            );
        }

        if (typeof value === 'object') {
            const entries = Object.entries(value);
            if (entries.length === 0) {
                return <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-400 rounded text-xs">empty object</span>;
            }
            return (
                <div className="inline-block ml-4 space-y-2">
                    {entries.map(([key, val]) => (
                        <div key={key} className="flex items-center gap-2 bg-brand-light rounded p-2 border border-brand w-fit">
                            <span className="inline-flex items-center px-2 py-1 bg-brand-light text-brand rounded text-xs font-semibold whitespace-nowrap">{capitalizeKey(key)}</span>
                            {renderValue(val, depth + 1)}
                        </div>
                    ))}
                </div>
            );
        }

        return <span className="inline-flex items-center px-2 py-1 bg-gray-50 text-gray-800 rounded text-xs border border-gray-200">{String(value)}</span>;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <LoadingSpinner />
            </div>
        );
    }

    if (!event) {
        return (
            <div className="space-y-6">
                <Card className="p-8 text-center">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Event Not Found</h2>
                    <p className="text-gray-600 mb-4">The event you're looking for doesn't exist or has been removed.</p>
                    <button onClick={() => navigate('/events')} className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-hover">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Events
                    </button>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            {/* Avoid transforms to keep overlays positioned correctly */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <button onClick={() => navigate('/events')} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
                            <ArrowLeft className="w-4 h-4" />
                            Back to Events
                        </button>
                    </div>
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">Event Details</h1>
                            <div className="flex items-center gap-2">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-brand-light text-brand">{event.eventName}</span>
                                <span className="text-gray-500">#{event.id}</span>
                            </div>
                        </div>
                    </div>
                </Card>
            </motion.div>

            {/* Event Information */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
                <Card className="p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Event Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Transaction Hash */}
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-500">Transaction Hash</label>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 px-3 py-2 bg-gray-50 rounded text-sm font-mono text-gray-900 border border-gray-200 break-all overflow-x-auto">{event.txHash}</code>
                                <button onClick={() => handleCopy(event.txHash, 'txHash')} className="p-2 hover:bg-gray-100 rounded transition-colors" title="Copy transaction hash">
                                    {copiedField === 'txHash' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-600" />}
                                </button>
                            </div>
                        </div>

                        {/* Block Number */}
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-500">Block Number</label>
                            <div className="px-3 py-2 bg-gray-50 rounded text-sm text-gray-900 border border-gray-200">{event.blockNumber?.toLocaleString() || 'N/A'}</div>
                        </div>

                        {/* Log Index */}
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-500">Log Index</label>
                            <div className="px-3 py-2 bg-gray-50 rounded text-sm text-gray-900 border border-gray-200">{event.logIndex ?? 'N/A'}</div>
                        </div>

                        {/* Timestamp */}
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-500">Timestamp</label>
                            <div className="px-3 py-2 bg-gray-50 rounded text-sm text-gray-900 border border-gray-200">{formatTimestamp(event.eventTimestamp)}</div>
                        </div>

                        {/* Contract Address */}
                        <div className="space-y-1 md:col-span-2">
                            <label className="text-sm font-medium text-gray-500">Contract Address</label>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 px-3 py-2 bg-gray-50 rounded text-sm font-mono text-gray-900 border border-gray-200 break-all overflow-x-auto">{event.contractAddress}</code>
                                <button onClick={() => handleCopy(event.contractAddress, 'contractAddress')} className="p-2 hover:bg-gray-100 rounded transition-colors" title="Copy contract address">
                                    {copiedField === 'contractAddress' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-600" />}
                                </button>
                            </div>
                        </div>
                    </div>
                </Card>
            </motion.div>

            {/* Event Data */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                <Card className="p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Event Data</h2>
                    {event.eventData && Object.keys(event.eventData).length > 0 ? (
                        <div className="bg-white rounded-lg border border-gray-300 p-4 shadow-sm overflow-x-auto">
                            <div className="text-sm leading-relaxed space-y-3 min-w-max">
                                {Object.entries(event.eventData).map(([key, value]) => (
                                    <div key={key} className="flex items-start gap-2 bg-brand-light rounded-lg p-3 border border-brand w-fit">
                                        <span className="inline-block px-2.5 py-1 bg-brand text-white rounded text-xs font-semibold whitespace-nowrap border border-brand">{capitalizeKey(key)}</span>
                                        <div className="">{renderValue(value, 0)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center text-gray-500 italic">No event data available</div>
                    )}
                </Card>
            </motion.div>

            {/* Metadata */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                <Card className="p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-500">Created At</label>
                            <div className="px-3 py-2 bg-gray-50 rounded text-sm text-gray-900 border border-gray-200">
                                {event.createdAt
                                    ? new Date(event.createdAt).toLocaleString('en-GB', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          second: '2-digit',
                                          hour12: true,
                                          timeZone: 'Asia/Kolkata',
                                      })
                                    : 'N/A'}
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-500">Updated At</label>
                            <div className="px-3 py-2 bg-gray-50 rounded text-sm text-gray-900 border border-gray-200">
                                {event.updatedAt
                                    ? new Date(event.updatedAt).toLocaleString('en-GB', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          second: '2-digit',
                                          hour12: true,
                                          timeZone: 'Asia/Kolkata',
                                      })
                                    : 'N/A'}
                            </div>
                        </div>
                    </div>
                </Card>
            </motion.div>
        </div>
    );
};

export default EventDetail;
